import {
    AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild
} from '@angular/core';
import {Store} from '@ngrx/store';
import VideoState from './modules/shared/stores/root-store/video.state';
import {
    BeginGetVideoAction,
    SetIsInMobilePreviewMode,
    SetIsInPreviewModeAction,
    SetIsIOSFullScreenDisable,
    SetVideoStateTypeAction
} from './modules/shared/stores/root-store/actions/video.action';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {BandwidthService} from './modules/shared/services/bandwidth.service';
import {HttpService} from './modules/shared/services/http.service';
import {EventTrackingService} from './modules/shared/services/event-tracking.service';
import {EventEnum} from './enums/event.enum';
import {SessionService} from './modules/shared/services/session.service';
import {BrowserService} from './modules/shared/services/browser.service';
import {
    selectActualVideoState,
    selectIsInMobilePreviewMode,
    selectIsPreviewPurlFromState
} from './modules/shared/stores/root-store/selectors/video.selector';
import {VideoStateEnum} from './enums/video-state.enum';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {environment} from '../environments/environment';
import {Meta} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import EN_TRANS from '../assets/i18n/en-US.json';
import DE_TRANS from '../assets/i18n/de-DE.json';
import EL_TRANS from '../assets/i18n/el-GR.json';
import ES_TRANS from '../assets/i18n/es-419.json';
import FR_TRANS from '../assets/i18n/fr-FR.json';
import IT_TRANS from '../assets/i18n/it-IT.json';
import JA_TRANS from '../assets/i18n/ja-JP.json';
import NL_TRANS from '../assets/i18n/nl-NL.json';
import NO_TRANS from '../assets/i18n/no-NO.json';
import PT_TRANS from '../assets/i18n/pt-BR.json';
import SV_TRANS from '../assets/i18n/sv-SE.json';

@Component({
    selector: 'eocm-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
    // root fonts are calculated as 14px relative to 960x540
    readonly HORIZONTAL_ROOT_FONT_SIZE_SCALE = 2.59259; // 14px / 540px
    readonly VERTICAL_ROOT_FONT_SIZE_SCALE = 1.45833; // 14px / 960px

    windowDesiredAspectRatio: number = 16 / 9;
    preview: string = null;
    isInMobilePreviewMode = false;
    videoState;
    reporting = false;
    isLastOrientationPortrait: boolean;
    isPreviewPurl = false;

    @ViewChild('videoContainer') videoPlayerContainer: ElementRef;

    constructor(
        private store: Store<{ video: VideoState }>,
        private activatedRoute: ActivatedRoute,
        private bandwidthService: BandwidthService,
        private sessionService: SessionService,
        private tracking: EventTrackingService,
        private browserService: BrowserService,
        private httpService: HttpService,
        private meta: Meta,
        private translate: TranslateService
    ) {}

    @HostListener('window:blur')
    onBlur() {
        if (this.browserService.isMobileDevice() && this.videoState !== VideoStateEnum.Interaction) {
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.UserPause
            }));
        }
    }

    ngOnInit(): void {
        this.initTranslateSettings();

        if (this.browserService.isMobileDevice()) {
            this.isLastOrientationPortrait = this.browserService.isOrientationPortrait();
        }

        this.activatedRoute.params.subscribe(async (urlParams) => {
            const params = this.getParamsCombined(urlParams, this.activatedRoute.snapshot.queryParamMap);

            this.reporting = params.get('reporting') === 'true';
            this.preview = params.get('preview');

            if (params.has('region')) {
                this.httpService.setRegion(params.get('region'));
            }

            this.setEventsSending(params);

            if (params.get('videoId')) {

                const originURL = params.get('originUrl') ? params.get('originUrl') : window.location.href;
                console.log(originURL);

                this.sessionService.initialize(
                    params.get('videoId'),
                    params.get('dataUID'),
                    originURL,
                    params.get('publish'),
                    !!params.get('preview')
                );
                this.tracking.track(EventEnum.PlayerInit);
                await this.sessionService.save({
                    isInitalSave: true
                });

                await this.bandwidthService.detect();

                if (this.preview) {
                    this.store.dispatch(SetIsInPreviewModeAction({
                        preview: true
                    }));
                }

                if (params.get('forceMobile')) {
                    this.store.dispatch(SetIsInMobilePreviewMode({
                        isInMobilePreviewMode: true
                    }));
                }

                FingerprintJS.load()
                    .then(fingerprintAgent => fingerprintAgent.get())
                    .then(fingerprint => {
                        params.set('fingerprint', fingerprint.visitorId);
                        params.set('sessionId', this.sessionService.getLastSession());
                        // b = bandwidth - shorter param name for GET URL
                        params.set('b', this.bandwidthService.mbps.toString())

                        this.store.dispatch(BeginGetVideoAction({
                            videoId: params.get('videoId'), params
                        }));
                    });
            }

            if (params.get('iosFullscreen') === 'true') {
                this.store.dispatch(SetIsIOSFullScreenDisable({
                    isIOSFullScreenDisable: false
                }));
            }
        });

        this.store.select(selectIsInMobilePreviewMode).subscribe((isInMobilePreviewMode) => {
            this.isInMobilePreviewMode = isInMobilePreviewMode;
        });

        this.store.select(selectActualVideoState).subscribe(state => {
            this.videoState = state;
        });

        this.store.select(selectIsPreviewPurlFromState).subscribe((purlPreview) => {
            this.isPreviewPurl = purlPreview;
        });

        // Prevents zoom in on input focus on iOS devices
        if (this.browserService.isAppleMobileDevice()) {
            this.meta.updateTag({
                name: 'viewport',
                content: 'width=device-width, initial-scale=1, maximum-scale=1'
            })
        }
    }

    ngAfterViewInit(): void {
        window.dispatchEvent(new Event('resize'));
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const viewportSize = this.getViewportSize();
        const windowCurrentAspectRatio = viewportSize.vw / viewportSize.vh;
        const videoContainer: HTMLElement = this.videoPlayerContainer.nativeElement;
        const isViewportHorizontal = windowCurrentAspectRatio > this.windowDesiredAspectRatio;
        const isNowPortrait = this.browserService.isOrientationPortrait()

        if (this.browserService.isMobileDevice()) {
            if (this.getCurrentActiveElementTag() !== 'BODY' && !isNowPortrait) {
                if (isNowPortrait !== this.isLastOrientationPortrait) {
                    (document.activeElement as HTMLElement).blur();
                    this.onResize({
                    });
                } else {
                    document.activeElement.scrollIntoView();
                }

                this.isLastOrientationPortrait = isNowPortrait;

                return;
            }
        }

        this.isLastOrientationPortrait = isNowPortrait;

        if (this.browserService.isMobileDevice() && isViewportHorizontal) {
            this.resizePlayerForHorizontalMobile(videoContainer, viewportSize);

            return;
        }

        if (isViewportHorizontal) {
            this.resizePlayerForHorizontal(videoContainer, viewportSize);
        } else {
            this.resizePlayerForPortrait(videoContainer, viewportSize);
        }
    }

    getViewportSize() {
        const vw = window.innerWidth && document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : document.documentElement.clientWidth ||
      document.getElementsByTagName('body')[0].clientWidth;
        const vh = window.innerHeight && document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : document.documentElement.clientHeight ||
      document.getElementsByTagName('body')[0].clientHeight;
        return {
            vw, vh
        };
    }

    reloadCss() {
        const links = Array.from(document.getElementsByTagName('link'));

        for (const link of links) {
            if (link.rel === 'stylesheet') {
                link.href += '';
            }
        }
    }

    getWarningMessage(): string | null {
        if (this.reporting) {
            return 'MESSAGES.SAMPLE_REPORTING';
        }

        if (this.isPreviewPurl) {
            return 'MESSAGES.SAMPLE_PREVIEW';
        }

        return null;
    }

    private initTranslateSettings() {
        this.translate.addLangs([
            'en-US',
            'de-DE',
            'el-GR',
            'es-419',
            'fr-FR',
            'it-IT',
            'ja-JP',
            'nl-NL',
            'no-NO',
            'pt-BR',
            'sv-SE'
        ]);
        this.translate.setDefaultLang('en-US');
        
        this.translate.setTranslation('en-US', EN_TRANS);
        this.translate.setTranslation('de-DE', DE_TRANS);
        this.translate.setTranslation('el-GR', EL_TRANS);
        this.translate.setTranslation('es-419', ES_TRANS);
        this.translate.setTranslation('fr-FR', FR_TRANS);
        this.translate.setTranslation('it-IT', IT_TRANS);
        this.translate.setTranslation('ja-JP', JA_TRANS);
        this.translate.setTranslation('nl-NL', NL_TRANS);
        this.translate.setTranslation('no-NO', NO_TRANS);
        this.translate.setTranslation('pt-BR', PT_TRANS);
        this.translate.setTranslation('sv-SE', SV_TRANS);

        const pageLang = document.documentElement.lang;
        const isSupported = this.translate.getLangs().includes(pageLang);
        const langToUse = isSupported ? pageLang : this.translate.getDefaultLang();
        this.translate.use(langToUse);
    }

    private getFontSizeInPxForHorizontalViewPort(vh) {
        return ((this.HORIZONTAL_ROOT_FONT_SIZE_SCALE * vh) / 100);
    }

    private getParamsCombined(urlParams, queryParamMap: ParamMap): Map<string, string> {
        const params = new Map<string, string>();

        queryParamMap.keys.map((key) => params.set(key, queryParamMap.get(key)));
        Object.keys(urlParams).map((key) => urlParams[key] && params.set(key, urlParams[key]));

        return params;
    }

    private setEventsSending(params) {
        let sendEvents: boolean = !this.preview && environment.sendEvents;

        if (this.reporting) {
            sendEvents = false;
        }

        if (params.has('sendEvents')) {
            sendEvents = !['0', 'false'].includes(params.get('sendEvents').toLowerCase());
        }

        this.sessionService.sendEvents = sendEvents;
        this.tracking.sendEvents = sendEvents;
        this.tracking.showLogs = !(!this.preview && environment.sendEvents);
    }

    private getCurrentActiveElementTag() {
        return document.activeElement.tagName;
    }

    private resizePlayerForHorizontalMobile(videoContainer, viewportSize) {
        const width = document.documentElement.clientHeight * this.windowDesiredAspectRatio;
        const margin = (document.documentElement.clientWidth - width) / 2 + 'px';

        this.reloadCss();

        videoContainer.style.height = document.documentElement.clientHeight + 'px';
        videoContainer.style.width = width + 'px';
        videoContainer.style.margin = `0 ${margin}`;
        document.documentElement.style.fontSize = (this.getFontSizeInPxForHorizontalViewPort(viewportSize.vh) - 0.6) + 'px';

    }

    private resizePlayerForHorizontal(videoContainer, viewportSize) {
        const width = viewportSize.vh * this.windowDesiredAspectRatio;
        const margin = (viewportSize.vw - width) / 2 + 'px';

        videoContainer.style.width = width + 'px';
        videoContainer.style.height = viewportSize.vh + 'px';
        videoContainer.style.margin = `0 ${margin}`;

        document.documentElement.style.fontSize = this.browserService.isAppleMobileDevice()
            ? this.getFontSizeInPxForHorizontalViewPort(viewportSize.vh) + 'px'
            : this.HORIZONTAL_ROOT_FONT_SIZE_SCALE + 'vh';
    }

    private resizePlayerForPortrait(videoContainer, viewportSize) {
        const height = viewportSize.vw / this.windowDesiredAspectRatio;
        const margin = (viewportSize.vh - height) / 2 + 'px';

        videoContainer.style.width = viewportSize.vw + 'px';
        videoContainer.style.height = height + 'px';
        videoContainer.style.margin = `${margin} 0`;
        document.documentElement.style.fontSize = this.VERTICAL_ROOT_FONT_SIZE_SCALE + 'vw';
    }
}
